const TRIGGER_SELECTOR = "[data-lightbox-src]";
const SWIPE_THRESHOLD = 50;

class SiteLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector("dialog");
    if (!this.dialog) return;

    this.img = this.querySelector(".site-lightbox__img");
    this.caption = this.querySelector(".site-lightbox__caption");
    this.counter = this.querySelector(".site-lightbox__counter");
    this.prevButton = this.querySelector(".site-lightbox__prev");
    this.nextButton = this.querySelector(".site-lightbox__next");

    this.slides = [];
    this.index = 0;
    this.pointerStart = null;

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handleClose = this.handleClose.bind(this);

    document.addEventListener("click", this.handleDocumentClick);
    this.dialog.addEventListener("close", this.handleClose);
    this.dialog.addEventListener("pointerdown", this.handlePointerDown);
    this.dialog.addEventListener("pointerup", this.handlePointerUp);

    this.querySelector(".site-lightbox__close").addEventListener("click", () =>
      this.dialog.close()
    );
    this.prevButton.addEventListener("click", () => this.#move(-1));
    this.nextButton.addEventListener("click", () => this.#move(1));

    // Safari has no `closedby` support yet, so light-dismiss needs a fallback.
    if (!("closedBy" in HTMLDialogElement.prototype)) {
      this.dialog.addEventListener("click", (event) =>
        this.#handleBackdropClick(event)
      );
    }
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("keydown", this.handleKeydown);
  }

  handleDocumentClick(event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (!trigger || this.dialog.contains(trigger)) return;

    event.preventDefault();
    this.#open(trigger);
  }

  handleKeydown(event) {
    if (event.key === "ArrowLeft") this.#move(-1);
    if (event.key === "ArrowRight") this.#move(1);
  }

  handlePointerDown(event) {
    if (event.pointerType === "mouse") return;
    this.pointerStart = { x: event.clientX, y: event.clientY };
  }

  handlePointerUp(event) {
    if (!this.pointerStart) return;

    const dx = event.clientX - this.pointerStart.x;
    const dy = event.clientY - this.pointerStart.y;
    this.pointerStart = null;

    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dy) > SWIPE_THRESHOLD) return;
    this.#move(dx < 0 ? 1 : -1);
  }

  handleClose() {
    document.removeEventListener("keydown", this.handleKeydown);
    this.slides = [];
  }

  #resolveSrc(el) {
    return el.dataset.lightboxSrc || el.getAttribute("href") || "";
  }

  #collectSlides(trigger) {
    const group = trigger.dataset.lightboxGroup;
    const triggers = group
      ? [
          ...document.querySelectorAll(
            `${TRIGGER_SELECTOR}[data-lightbox-group="${CSS.escape(group)}"]`
          ),
        ]
      : [trigger];

    // Deduping by src drops Swiper's loop clones without this component
    // needing to know anything about Swiper.
    const seen = new Set();

    return triggers.reduce((slides, el) => {
      const src = this.#resolveSrc(el);
      if (!src || seen.has(src)) return slides;

      seen.add(src);
      slides.push({
        src,
        alt: el.querySelector("img")?.alt ?? "",
        caption: el.dataset.lightboxCaption ?? "",
      });
      return slides;
    }, []);
  }

  #open(trigger) {
    if (this.dialog.open) return;

    const slides = this.#collectSlides(trigger);
    const index = slides.findIndex(
      (slide) => slide.src === this.#resolveSrc(trigger)
    );
    if (index === -1) return;

    this.slides = slides;

    const solo = slides.length === 1;
    this.prevButton.hidden = solo;
    this.nextButton.hidden = solo;
    this.counter.hidden = solo;

    this.#show(index);
    document.addEventListener("keydown", this.handleKeydown);
    this.dialog.showModal();
  }

  #show(index) {
    this.index = index;

    const { src, alt, caption } = this.slides[index];
    this.img.src = src;
    this.img.alt = alt;

    const text = caption || alt;
    this.caption.textContent = text;
    this.caption.hidden = !text;
    // A caption that merely repeats the alt would be announced twice.
    this.caption.setAttribute("aria-hidden", caption ? "false" : "true");

    this.counter.textContent = `${index + 1} / ${this.slides.length}`;
  }

  #move(delta) {
    const total = this.slides.length;
    if (total < 2) return;

    this.#show((this.index + delta + total) % total);
  }

  #handleBackdropClick(event) {
    if (event.target !== this.dialog) return;

    const rect = this.dialog.getBoundingClientRect();
    const insideContent =
      rect.top <= event.clientY &&
      event.clientY <= rect.top + rect.height &&
      rect.left <= event.clientX &&
      event.clientX <= rect.left + rect.width;

    if (!insideContent) this.dialog.close();
  }
}

if (!customElements.get("site-lightbox")) {
  customElements.define("site-lightbox", SiteLightbox);
}
